import React, {useState, useEffect} from 'react'

import Navbar from '../../Components/Navbar/Navbar'
import BtnExcel from '../../Components/BtnExcel';
import AntDTable from '../../Components/Table/AntDTable';
import dayjs from 'dayjs';

import { 
  Layout,
  Col, 
  Row,
  Button,
  Dropdown, 
  Space,
  Modal,
  Tabs,
  Input,
  DatePicker,
} from 'antd';

import { useNavigate, useLocation } from 'react-router-dom';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { MoreVert  } from '@mui/icons-material';
import { UseGetRequest } from '../../Utils/api/ApiRequests'
import { usePostRequest } from '../../hooks/usePostRequest'
import { 
  formatDate, 
  formatAmount, 
  getUserRole, 
  getTodayDateISO, 
  getStartDate, 
  getEndDate, 
  getTabkey 
} from '../../Utils/Common';

const { TextArea } = Input;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;
const { Search } = Input;
const { confirm } = Modal;
  
const OwnerBilling = () => {
  const role_name = getUserRole();
  const location = useLocation();
  const start_date = getStartDate();
  const end_date = getEndDate();
  const tab_key = getTabkey();
  const navigate = useNavigate();

  const [visible, setVisible] = useState(false);
  const [billingId, setBillingId] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true)
  const [remarks, setRemarks] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [pageSize, setPageSize] = useState('');
  const [billingData, setBillingData] = useState([]);
  const [excelData, setExcelData] = useState([]);
  const [totalAmountDue, setTotalAmountDue] = useState(0);
  const [totalPaidAmount, setTotalPaidAmount] = useState(0);
  const [totalBalance, setTotalBalance] = useState(0);
  const startDate = dayjs().startOf('month');
  const endDate = dayjs().endOf('month');

  const [filterConfig, setFilterConfig] = useState({
    payment_id: "",
    type: "owner",
    start_date: start_date ? start_date : dayjs(startDate).format('YYYY-MM-DD'),
    end_date: end_date ? end_date : dayjs(endDate).format('YYYY-MM-DD'),
    status: location?.state ? location.state : tab_key ? tab_key : "all",
    page: 1,
  });

  const { callPost, loading } = usePostRequest(()=>{
    fetchOwnerBilling();
    setVisible(false);
  });

  function ActionBtn(row) {
    const items = [
      {
        label: (
          <div onClick={()=> navigate('/preview-billing/owner/' + row.row.billing_no)}>
            Print Preview
          </div>
        ),
        key: '1',
      },
      (row.row.status === "open" && parseInt(row.row.paid_amount) === 0) && {
        label: (
          <div style={{color: 'red'}} onClick={()=>showDeleteConfirm(row.row.billing_no)}>
            Void
          </div>
        ),
        key: '7',
      },
    ];

    return (
      <>
        {
          row.row.status !== "voided" && (
            <span className='center'>
              <Dropdown
                menu={{
                items,
                }}
                trigger={['click']}
              >
                <div type='primary' onClick={(e) => e.preventDefault()} className='action-btn'>
                  <Space>
                    <MoreVert />
                  </Space>
                </div>
              </Dropdown>
            </span>
          )
        }
      </>
    );
  };

  function RefundBtn(row) {
    return (
      <Row>
        <Col className='center'>
          <Button 
            className='approve-btn'
            onClick={() => handleForApproval({id: row.row.billing_no, action:"approve"})}
          >
            Approve
          </Button>
        </Col>
        <Col className='center'>
          <Button 
            className='disapprove-btn'
            onClick={() => handleForApproval({id:row.row.billing_no, action:"disapprove"})}
          >
            Disapprove
          </Button>
        </Col>
      </Row>
    );
  };

  const handleForApproval = (data) => {
    confirm({
      title: `Are you sure you want to ${data.action === "approve" ? "approve" : "disapprove"}?`,
      content: 'This action cannot be undone.',
      okText: 'Yes',
      cancelText: 'No',
      cancelButtonProps: {
        className: 'custom-no-button',
      },
      confirmLoading: loading,
      onOk() {
        if(data){
          const resetData = {
            billing_id: data.id,
            action: data.action
          };
  
          callPost(
            "billings/void_action", 
            resetData,
            false,
          );
        }
      },
    });
  };
  
  const handleRemarks = (info) => {
    setRemarks(info)
    setIsDisabled(info.trim() === '')
  };

  const handleOk = () => {
    callPost(
      "billings/delete",
      {
        billing_id: billingId,
        delete_reason: remarks,
      },
      false
    );
  };

  const showDeleteConfirm = (id) => {
    setVisible(true);
    setBillingId(id)
  };

  const onChange = (activeKey) => {
    localStorage.setItem('tabkey', JSON.stringify(activeKey));
    if(activeKey === "closed" || activeKey === "voided" || activeKey === "all"){
      setFilterConfig((prev) => (
        {
          ...prev,
          status:  activeKey,
          start_date: dayjs(startDate).format('YYYY-MM-DD'),
          end_date: dayjs(endDate).format('YYYY-MM-DD')
        }
      ));
      localStorage.setItem('start_date', JSON.stringify(startDate.format('YYYY-MM-DD')));
      localStorage.setItem('end_date', JSON.stringify(endDate.format('YYYY-MM-DD')));
    } else {
      setFilterConfig((prev) => (
        {
          ...prev,
          status:  activeKey,
          start_date: '',
          end_date: ''
        }
      ));
      localStorage.removeItem('start_date');
      localStorage.removeItem('end_date');
    }
  };
  
  const handleButtonClick = (id) => {
    const url = '/preview-payment/owner/' + id;
    window.open(url, '_blank');
  };

  async function fetchOwnerBilling() {
    setIsLoading(true)
    const response = await UseGetRequest("billings/get", filterConfig);
    if (response?.data) {
      const billing_data = response?.data?.data.map((data) => ({
        billing_no: parseInt(data.billing_no),
        tenant_id: data.tenant_id,
        owner_id: data.owner_id,
        tradename: data.tradename,
        concatenated_property: data.concatenated_property,
        date_paid: data.date_paid && formatDate(data.date_paid,),
        billing_date: data.billing_date && formatDate(data.billing_date),
        due_date: data.due_date && formatDate(data.due_date),
        amount: formatAmount(data.amount),
        paid_amount: formatAmount(data.paid_amount),
        balance: formatAmount(data.balance),
        prepared_by: data.prepared_by,
        delete_reason: data.delete_reason,
        type: data.type,
        remarks: data.remarks,
        status: data.status,
        payment: data?.payments?.map((data) => (
          <Button onClick={() => handleButtonClick(data.id)}>{data.doc_no}</Button>
        ))
      }))

      setTotalAmountDue(response?.data?.total_amount_due)
      setTotalPaidAmount(response?.data?.total_paid_amount)
      setTotalBalance(response?.data?.total_balance)
      setBillingData(billing_data);
      setPageSize(response?.data?.size)
    } else {
      setTotalAmountDue(0)
      setTotalPaidAmount(0)
      setTotalBalance(0)
      setBillingData([])
      setPageSize('')
    }
    setIsLoading(false)
  }

  async function fetchExportData() {
    setExportLoading(true)
    const response = await UseGetRequest("billings/get", 
      {
        payment_id: filterConfig.payment_id,
        type: filterConfig.type,
        start_date: filterConfig.start_date,
        end_date: filterConfig.end_date,
        status: filterConfig.status,
      }
    );
    if (response?.data) {
      const excel_data = response?.data?.data?.map((data) => ({
        billing_no: data.billing_no,
        tradename: data.tradename,
        concatenated_property: data.concatenated_property,
        billing_date: data.billing_date && formatDate(data.billing_date),
        due_date: data.due_date && formatDate(data.due_date),
        amount: data.amount && formatAmount(data.amount,),
        paid_amount: data.paid_amount && formatAmount(data.paid_amount),
        balance: data.balance && formatAmount(data.balance),
        prepared_by: data.prepared_by,
        status: data.status,
      }))

      setExcelData(excel_data)
      setExportLoading(false);
    } else {
      setExcelData([])
      setExportLoading(false);
    }
  }

  const customTitle = (
    <div style={{ display: 'flex', alignItems: 'center'}}>
      <ExclamationCircleFilled style={{ color: 'red', marginRight: 8, fontSize: '2rem'}} />
      <span>Permanently removing this tenant billing<br/>Are you sure you want to continue?</span>
    </div>
  );

  const onSearchDate = (e) => {
    if(e){
      setFilterConfig((prev) => (
        {
          ...prev,
          start_date: e[0].format("YYYY-MM-DD"),
          end_date: e[1].format("YYYY-MM-DD"),
          page: 1,
        }
      ));
      localStorage.setItem('start_date', JSON.stringify(e[0].format("YYYY-MM-DD")));
      localStorage.setItem('end_date', JSON.stringify(e[1].format("YYYY-MM-DD")));
    } else {
      setFilterConfig((prev) => (
        {
          ...prev,
          start_date: '',
          end_date: '',
          page: 1,
        }
      ));
      localStorage.removeItem('start_date');
      localStorage.removeItem('end_date');
    }
  };

  const handleSearchChange = (e) => {
    setFilterConfig((prev) => (
      {
        ...prev,
        search: e || '',
        page: 1,
      }
    ));
  };
  
  const handleEmpty = (e) => {
    if(e.target.value === ''){
      setFilterConfig((prev) => (
        {
          ...prev,
          search: e.target.value || '',
          page: 1,
        }
      ));
    }
  };  

  const handleTableChange = (pagination) => {
    setFilterConfig((prev) => ({
      ...prev,
      page: parseInt(pagination.current),
      page_size: pagination.pageSize
      // total_records_per_page: pagination.pageSize,
    }));
  };

  useEffect(() => {
    fetchOwnerBilling();
    fetchExportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterConfig]);
  
  return (
    <Layout className='page'>
      <Navbar navKey={'owners_billings_nav'}/>
      <Layout className='content-container'>
        <Row className='custom-padding responsive-inline responsive-tab-inline'>
          <Col span={9} className='header-container'>
              OWNER BILLING
          </Col>
          <Col span={15} className='mt-2 responsive-tab-width' align='end'>
              <Space className='responsive-inline'>
                <BtnExcel
                  label={"Export to Excel"}
                  size={"sm"}
                  excelData={excelData}
                  xlsxHeaders={[
                    "ID",
                    "Tradename",
                    "Building & Unit Number",
                    "Billing Date",
                    "Due Date",
                    "Amount",
                    "Paid Amount",
                    "Balance",
                    "Prepared By",
                    "Status",
                  ]}
                  handleData={fetchExportData}
                  isClicked={exportLoading}
                  fileName={`Owner_Billings_${getTodayDateISO()}.xlsx`}
                />
                <Button 
                  className='button-color btn-header' 
                  onClick={() => navigate('/add-billing/owner')} 
                  type="primary"
                >
                  + Add Billing
                </Button>
              </Space>
          </Col>
        </Row>
        <Row className='mb-4'>
          <Space span={24} className='responsive-inline excel-row'>
            <Search 
              placeholder="Search..." 
              size='medium' 
              onSearch={(e)=>handleSearchChange(e)}
              onChange={(e)=>handleEmpty(e)}
              className='responsive-header-input'
            /> 
            <RangePicker 
              renderExtraFooter={() => 'extra footer'} 
              className='responsive-header-input'
              value={[
                filterConfig?.start_date ? dayjs(filterConfig.start_date) : null,
                filterConfig?.end_date ? dayjs(filterConfig.end_date) : null
              ]}
              onChange={(e)=>onSearchDate(e)}
            />
          </Space>
        </Row>
        <Row gutter={[16, 16]} className='ms-4'>
          <Col xs={24} sm={24} md={8} lg={8} xl={8} className='amount-total-two' align='left'>
              Total Amount: {formatAmount(totalAmountDue)}
          </Col>
      
          <Col xs={24} sm={24} md={8} lg={8} xl={8} className='amount-total-two' align='left'>
              Total Paid Amount: {formatAmount(totalPaidAmount)}
          </Col>
    
          <Col xs={24} sm={24} md={8} lg={8} xl={8} className='amount-total-two' align='left'>
              Total Balance: {formatAmount(totalBalance)}
          </Col>
        </Row>
        <Row className='page-content'>
          <Col span={24} className='responsive-overflow-auto'>
            <Tabs 
              type="card" 
              className="custom-tabs"
              activeKey={filterConfig?.status}
              onChange={onChange}
            >
              <TabPane tab="All" key="all">
                <AntDTable
                  tableHeaders={[
                    "Billing Number",
                    "Building & Unit Number",
                    "Period Covered",
                    "Owner Name",
                    "Billing Date",
                    "Due Date",
                    "Amount",
                    "Payment",
                    "Paid Amount",
                    "Paid Date",
                    "Remarks",
                    "Prepared By",
                    "Status",
                    "Void Reason",
                  ]}
                  headerSelector={[
                    "billing_no",
                    "concatenated_property",
                    "period_covered",
                    "tradename",
                    "billing_date",
                    "due_date",
                    "amount",
                    "payment",
                    "paid_amount",
                    "date_paid",
                    "remarks",
                    "prepared_by",
                    "status",
                    "delete_reason",
                  ]}
                  tableData={billingData}
                  ActionBtn={(row) => ActionBtn(row)}
                  withAction={true}
                  loading={isLoading}
                  tablePages={10}
                  handleTableChange={handleTableChange}
                  filterConfig={filterConfig}
                  totalSize={pageSize}
                  isSearch={true}
                />
              </TabPane>
              <TabPane tab="Open Bill" key="open">
                <AntDTable
                  tableHeaders={[
                    "Billing Number",
                    "Building & Unit Number",
                    "Period Covered",
                    "Owner Name",
                    "Billing Date",
                    "Due Date",
                    "Amount",
                    "Paid Amount",
                    "Payment",
                    "Balance",
                    "Remarks",
                    "Prepared By",
                  ]}
                  headerSelector={[
                    "billing_no",
                    "concatenated_property",
                    "period_covered",
                    "tradename",
                    "billing_date",
                    "due_date",
                    "amount",
                    "paid_amount",
                    "payment",
                    "balance",
                    "remarks",
                    "prepared_by",
                  ]}
                  tableData={billingData}
                  ActionBtn={(row) => ActionBtn(row)}
                  withAction={true}
                  loading={isLoading}
                  tablePages={10}
                  handleTableChange={handleTableChange}
                  filterConfig={filterConfig}
                  totalSize={pageSize}
                  isSearch={true}
                />
              </TabPane>
              <TabPane tab="Due Bill" key="due">
                <AntDTable
                  tableHeaders={[
                    "Billing Number",
                    "Building & Unit Number",
                    "Period Covered",
                    "Owner Name",
                    "Billing Date",
                    "Due Date",
                    "Amount",
                    "Paid Amount",
                    "Payment",
                    "Balance",
                    "Remarks",
                    "Prepared By",
                  ]}
                  headerSelector={[
                    "billing_no",
                    "concatenated_property",
                    "period_covered",
                    "tradename",
                    "billing_date",
                    "due_date",
                    "amount",
                    "paid_amount",
                    "payment",
                    "balance",
                    "remarks",
                    "prepared_by",
                  ]}
                  tableData={billingData}
                  ActionBtn={(row) => ActionBtn(row)}
                  withAction={true}
                  loading={isLoading}
                  tablePages={10}
                  handleTableChange={handleTableChange}
                  filterConfig={filterConfig}
                  totalSize={pageSize}
                  isSearch={true}
                />
              </TabPane>
              <TabPane tab="Closed Bill" key="closed">
                <AntDTable
                  tableHeaders={[
                    "Billing Number",
                    "Building & Unit Number",
                    "Period Covered",
                    "Owner Name",
                    "Billing Date",
                    "Due Date",
                    "Amount",
                    "Paid Amount",
                    "Paid Date",
                    "Payment",
                    "Remarks",
                    "Prepared By",
                  ]}
                  headerSelector={[
                    "billing_no",
                    "concatenated_property",
                    "period_covered",
                    "tradename",
                    "billing_date",
                    "due_date",
                    "amount",
                    "paid_amount",
                    "date_paid",
                    "payment",
                    "remarks",
                    "prepared_by",
                  ]}
                  tableData={billingData}
                  ActionBtn={(row) => ActionBtn(row)}
                  withAction={true}
                  loading={isLoading}
                  tablePages={10}
                  handleTableChange={handleTableChange}
                  filterConfig={filterConfig}
                  totalSize={pageSize}
                  isSearch={true}
                />
              </TabPane>
              <TabPane tab="For Approval" key="for_approval">
                <AntDTable
                  tableHeaders={[
                    "Billing Number",
                    "Building & Unit Number",
                    "Period Covered",
                    "Owner Name",
                    "Billing Date",
                    "Due Date",
                    "Amount",
                    "Payment",
                    "Remarks",
                    "Prepared By",
                  ]}
                  headerSelector={[
                    "billing_no",
                    "concatenated_property",
                    "period_covered",
                    "tradename",
                    "billing_date",
                    "due_date",
                    "amount",
                    "payment",
                    "remarks",
                    "prepared_by",
                  ]}
                  tableData={billingData}
                  ActionBtn={(row) => RefundBtn(row)}
                  withAction={role_name === "Administrator"}
                  loading={isLoading}
                  tablePages={10}
                  handleTableChange={handleTableChange}
                  filterConfig={filterConfig}
                  totalSize={pageSize}
                  isSearch={true}
                />
              </TabPane>
              <TabPane tab="Voided" key="voided">
                <AntDTable
                  tableHeaders={[
                    "Billing Number",
                    "Building & Unit Number",
                    "Period Covered",
                    "Owner Name",
                    "Billing Date",
                    "Due Date",
                    "Amount",
                    "Remarks",
                    "Prepared By",
                    "Void Remarks",
                  ]}
                  headerSelector={[
                    "billing_no",
                    "concatenated_property",
                    "period_covered",
                    "tradename",
                    "billing_date",
                    "due_date",
                    "amount",
                    "remarks",
                    "prepared_by",
                    "delete_reason",
                  ]}
                  tableData={billingData}
                  ActionBtn={(row) => ActionBtn(row)}
                  withAction={false}
                  loading={isLoading}
                  tablePages={10}
                  handleTableChange={handleTableChange}
                  filterConfig={filterConfig}
                  totalSize={pageSize}
                  isSearch={true}
                />
              </TabPane>
            </Tabs>
          </Col>
        </Row>
      </Layout>
      <Modal
        title={customTitle}
        open={visible}
        icon={<ExclamationCircleFilled style={{ color: 'red' }} />}
        onOk={handleOk}
        okText="Yes"
        okType="danger"
        cancelText="No"
        okButtonProps={{ disabled: isDisabled }}
        confirmLoading={loading}
        onCancel={() => setVisible(false)}
      >
        <div>
          <span style={{ color: 'red'}}>*</span>
          Remarks
        </div>
        <TextArea
          onChange={(e) => handleRemarks(e.target.value)}
        />
      </Modal>
    </Layout>
  )
}  
export default OwnerBilling
  